import { Component, inject, signal, input, output, effect, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormArray } from '@angular/forms';
import { MembershipService } from '../../../core/services/membership.service';
import { AppModal } from '../../../shared/components/app-modal/app-modal';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { LoadingService } from '../../../core/services/loading.service';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-membership-plan-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppModal, AppButton],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <app-modal [isOpen]="isOpen()" (isOpenChange)="close()" 
               [title]="plan() ? 'Editar Plan: ' + plan()?.name : 'Nuevo Plan de Membresía'"
               icon="solar:bill-list-bold-duotone">
      
      <form [formGroup]="form" (ngSubmit)="save()" class="space-y-6 pt-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Code -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Código Único</label>
            <input type="text" formControlName="code" placeholder="p.ej. GOLD_ANNUAL"
                   class="w-full h-12 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all uppercase" />
          </div>

          <!-- Name -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre Comercial</label>
            <input type="text" formControlName="name" placeholder="p.ej. Plan Oro Anual"
                   class="w-full h-12 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Monthly Price -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Precio Mensual (USD)</label>
            <input type="number" formControlName="monthlyPrice" step="0.01"
                   class="w-full h-12 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Annual Discount -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Descuento Anual (%)</label>
            <input type="number" formControlName="annualDiscountPercentage" 
                   class="w-full h-12 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Monthly Discount -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Descuento Mensual (%)</label>
            <input type="number" formControlName="monthlyDiscountPercentage" 
                   class="w-full h-12 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Description -->
          <div class="md:col-span-2 flex flex-col gap-1.5">
            <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Descripción Corta</label>
            <input type="text" formControlName="description" placeholder="p.ej. Ideal para consultorios pequeños"
                   class="w-full h-12 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
          </div>

          <!-- Active Toggle -->
          <div class="md:col-span-2 flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800">
             <div class="flex flex-col">
                <span class="text-xs font-bold text-foreground">Plan Activo</span>
                <span class="text-[10px] text-foreground/50 font-medium">Determina si el plan es visible para los negocios</span>
             </div>
             <input type="checkbox" formControlName="isActive" class="w-5 h-5 accent-primary" />
          </div>

          <!-- Features (FormArray) -->
          <div class="md:col-span-2 space-y-3">
             <div class="flex items-center justify-between">
                <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Características / Beneficios</label>
                <app-button variant="glass" size="xs" (btnClick)="addFeature()" iconLeft="solar:add-circle-bold">Agregar</app-button>
             </div>
             
             <div formArrayName="features" class="space-y-2">
                <div *ngFor="let feat of features.controls; let i=index" class="flex gap-2">
                    <input [formControlName]="i" type="text" placeholder="Beneficio del plan..."
                           class="flex-1 h-10 px-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium focus:ring-2 focus:ring-primary/20 outline-none" />
                    <app-button variant="glass" size="xs" (btnClick)="removeFeature(i)" [square]="true" iconLeft="solar:trash-bin-minimalistic-bold" color="text-rose-500"></app-button>
                </div>
             </div>
          </div>
        </div>
      </form>

      <div actions class="flex justify-end gap-3 w-full">
        <app-button variant="glass" size="sm" (btnClick)="close()">Cancelar</app-button>
        <app-button variant="primary" size="sm" [disabled]="form.invalid || loading()" (btnClick)="save()">
          {{ loading() ? 'Guardando...' : (plan() ? 'Actualizar Plan' : 'Crear Plan') }}
        </app-button>
      </div>
    </app-modal>
  `
})
export class MembershipPlanDialog {
  private fb = inject(FormBuilder);
  private membershipService = inject(MembershipService);
  private loadingService = inject(LoadingService);
  private toast = inject(ToastService);

  isOpen = input<boolean>(false);
  plan = input<any>(null);
  onClose = output<void>();
  onSave = output<any>();

  form = this.fb.group({
    code: ['', [Validators.required, Validators.pattern(/^[A-Z0-9_]+$/)]],
    name: ['', Validators.required],
    monthlyPrice: [0, [Validators.required, Validators.min(0)]],
    annualDiscountPercentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    monthlyDiscountPercentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    description: [''],
    currency: ['USD'],
    isActive: [true],
    features: this.fb.array([])
  });

  loading = signal(false);

  get features() {
    return this.form.get('features') as FormArray;
  }

  constructor() {
    effect(() => {
      const p = this.plan();
      if (this.isOpen()) {
        if (p) {
          // Reset array first
          while (this.features.length) this.features.removeAt(0);
          
          this.form.patchValue({
            code: p.code,
            name: p.name,
            monthlyPrice: p.monthlyPrice,
            annualDiscountPercentage: p.annualDiscountPercentage,
            monthlyDiscountPercentage: p.monthlyDiscountPercentage || 0,
            description: p.description || '',
            currency: p.currency || 'USD',
            isActive: p.isActive !== false
          });

          if (p.features && Array.isArray(p.features)) {
            p.features.forEach((f: string) => this.addFeature(f));
          }
        } else {
          this.form.reset({
            code: '',
            name: '',
            monthlyPrice: 0,
            annualDiscountPercentage: 0,
            monthlyDiscountPercentage: 0,
            description: '',
            currency: 'USD',
            isActive: true
          });
          while (this.features.length) this.features.removeAt(0);
          this.addFeature(); // Add one empty feature by default
        }
      }
    });
  }

  addFeature(value: string = '') {
    this.features.push(this.fb.control(value));
  }

  removeFeature(index: number) {
    this.features.removeAt(index);
  }

  close() {
    this.onClose.emit();
  }

  save() {
    if (this.form.invalid) return;

    this.loading.set(true);
    this.loadingService.showLoading('Guardando', 'Procesando plan de membresía...');

    const val = this.form.value;
    const obs$ = this.plan() 
        ? this.membershipService.updatePlan(this.plan()._id, val)
        : this.membershipService.createPlan(val);

    obs$.subscribe({
      next: (res) => {
        this.toast.show(this.plan() ? 'Plan actualizado' : 'Plan creado correctamente', 'success');
        this.onSave.emit(res);
        this.close();
        this.loading.set(false);
        this.loadingService.hideLoading();
      },
      error: () => {
        this.loading.set(false);
        this.loadingService.hideLoading();
      }
    });
  }
}
